export type Point = readonly [number, number];
export type MoveSpec = { name: string; glyph: string; steps?: Point[]; rays?: Point[]; captures?: Point[]; size?: number; intersection?: boolean; paths?: Point[][] };
const INK = '#252725', PAPER = '#fffefa';
export const ORTH: Point[] = [[0,-1],[1,0],[0,1],[-1,0]];
export const DIAG: Point[] = [[-1,-1],[1,-1],[1,1],[-1,1]];
export const EIGHT: Point[] = [...ORTH,...DIAG];
const GOLD: Point[] = [[-1,-1],[0,-1],[1,-1],[-1,0],[1,0],[0,1]];
export const CHESS_MOVES: MoveSpec[] = [
  {name:'キング',glyph:'♔',steps:EIGHT}, {name:'クイーン',glyph:'♕',rays:EIGHT},
  {name:'ルーク',glyph:'♖',rays:ORTH}, {name:'ビショップ',glyph:'♗',rays:DIAG},
  {name:'ナイト',glyph:'♘',steps:[[-1,-2],[1,-2],[-2,-1],[2,-1],[-2,1],[2,1],[-1,2],[1,2]]},
  {name:'ポーン',glyph:'♙',steps:[[0,-1]],captures:[[-1,-1],[1,-1]]},
];
export const SHOGI_MOVES: MoveSpec[] = [
  {name:'玉',glyph:'玉',steps:EIGHT}, {name:'飛',glyph:'飛',rays:ORTH}, {name:'角',glyph:'角',rays:DIAG},
  {name:'金',glyph:'金',steps:GOLD}, {name:'銀',glyph:'銀',steps:[[-1,-1],[0,-1],[1,-1],[-1,1],[1,1]]},
  {name:'桂',glyph:'桂',steps:[[-1,-2],[1,-2]]}, {name:'香',glyph:'香',rays:[[0,-1]]}, {name:'歩',glyph:'歩',steps:[[0,-1]]},
];
export const ANIMAL_MOVES: MoveSpec[] = [
  {name:'ライオン',glyph:'ラ',steps:EIGHT,size:3}, {name:'きりん',glyph:'キ',steps:ORTH,size:3},
  {name:'ぞう',glyph:'ぞ',steps:DIAG,size:3}, {name:'ひよこ',glyph:'ひ',steps:[[0,-1]],size:3},
  {name:'にわとり',glyph:'に',steps:GOLD,size:3},
];
export const XIANGQI_MOVES: MoveSpec[] = [
  {name:'将',glyph:'将',steps:ORTH,size:3}, {name:'士',glyph:'士',steps:DIAG,size:3},
  {name:'象',glyph:'象',steps:[[-2,-2],[2,-2],[-2,2],[2,2]]},
  {name:'車',glyph:'車',rays:ORTH}, {name:'馬',glyph:'馬',steps:[[-1,-2],[1,-2],[-2,-1],[2,-1],[-2,1],[2,1],[-1,2],[1,2]]},
  {name:'炮',glyph:'炮',rays:ORTH}, {name:'兵',glyph:'兵',steps:[[0,-1],[-1,0],[1,0]],size:3},
];
export const JANGGI_MOVES: MoveSpec[] = [
  {name:'将・士',glyph:'将',steps:EIGHT,size:3}, {name:'車',glyph:'車',rays:ORTH},
  {name:'馬',glyph:'馬',steps:CHESS_MOVES[4].steps},
  {name:'象',glyph:'象',steps:[[-2,-3],[2,-3],[-3,-2],[3,-2],[-3,2],[3,2],[-2,3],[2,3]],size:7},
  {name:'卒',glyph:'卒',steps:[[0,-1],[-1,0],[1,0]],size:3},
];


export function StoneLine({ count=5, label='', white=false, comparison=false }: {count?:number;label?:string;white?:boolean;comparison?:boolean}) {
  const columns=comparison?6:count, spacing=240/(columns-1);
  return <svg viewBox={comparison ? "0 0 308 74" : label ? "0 0 308 72" : "0 0 308 52"} role="img" aria-label={`${count}個が一直線に並んでいます。${label}`}>
    <path d="M8 30H300" stroke="#c1bdaf" />
    {Array.from({length:columns},(_,i)=><path key={`grid-${i}`} d={`M${34+i*spacing} 8V48`} stroke="#c1bdaf" />)}
    {Array.from({length:count},(_,i)=><circle key={`stone-${i}`} cx={34+i*spacing} cy="30" r="13" fill={white?PAPER:INK} stroke={INK} />)}
    {label&&<text x="154" y="70" textAnchor="middle" fontSize="13">{label}</text>}
  </svg>;
}

export function KyoenShapes() {
  return <svg viewBox="0 0 308 184" role="img" aria-label="直前に相手が置いた石を含む4個の石が、同じ円周か一直線上に並ぶ例です。二重丸が直前の石です。">
    {[0,1].map(i=><g key={i} transform={`translate(${i*154} 0)`}>
      {[0,1,2,3,4].map(j=><g key={j} stroke="#cbc8ba"><path d={`M10 ${12+j*34}H146`} /><path d={`M${10+j*34} 12V148`} /></g>)}
      {i?<path d="M8 10L116 118" stroke="#73766e" />:<circle cx="78" cy="80" r="34" fill="none" stroke="#73766e" />}
      {(i?[[10,12],[44,46],[78,80],[112,114]]:[[78,46],[44,80],[112,80],[78,114]]).map(([x,y],j)=><g key={j}><circle cx={x} cy={y} r="10" fill={INK} />{j===3&&<circle cx={x} cy={y} r="14" fill="none" stroke={INK} />}</g>)}
      <text x="78" y="178" textAnchor="middle" fontSize="14">{i?'一直線':'円周'}</text>
    </g>)}
  </svg>;
}

export function AmazonsSteps() {
  return <svg viewBox="0 0 308 120" role="img" aria-label="自分の白のコマを右へ2マス動かし、移動先から左上へ2マス先に矢を撃ちます。移動と矢で1手です。">
    {[0,1,2].map(step=><g key={step} transform={`translate(${step*106} 0)`}>
      {[0,1,2,3].map(i=><g key={i} stroke="#c7c3b6"><path d={`M1 ${3+i*28}H85`} /><path d={`M${1+i*28} 3V87`} /></g>)}
      <circle cx={step?71:15} cy="73" r="10" fill={PAPER} stroke={INK} />
      {step===2&&<path d="M9 11L21 23M21 11L9 23" stroke={INK} strokeWidth="2" />}
      <text x="43" y="111" textAnchor="middle" fontSize="13">{['動く前','コマを動かす','矢を撃つ'][step]}</text>
    </g>)}
  </svg>;
}

export function AbalonePush() {
  return <svg viewBox="0 0 308 142" role="img" aria-label="盤の端で黒2個が白1個を押します。白が盤外へ出る前後です。">
    {[0,1].map(step=><g key={step} transform={`translate(0 ${step*56})`}>
      <text x="5" y="28" fontSize="13">{step?'押した後':'押す前'}</text>
      <path d="M247 2V49" stroke="#777a70" strokeWidth="2" />
      {[0,1,2,3].map(i=><circle key={i} cx={136+i*43} cy="26" r="19" fill="none" stroke={i===3?'#c5c1b5':'#b5b8ac'} strokeDasharray={i===3?'3 3':undefined} />)}
      {[0,1].map(i=><circle key={i} cx={136+(i+step)*43} cy="26" r="15" fill={INK} />)}
      <circle cx={222+step*43} cy="26" r="15" fill={PAPER} stroke={INK} />
    </g>)}
    <text x="277" y="14" textAnchor="middle" fontSize="13">盤の外</text>
    <text x="154" y="138" textAnchor="middle" fontSize="14">白の玉を6個押し出しました。</text>
  </svg>;
}

const AMAZONS_END = (()=>{
  const board=Array<number>(100).fill(0);
  for(const i of [42,22,27,72])board[i]=1;
  for(const i of [44,0,9,99])board[i]=2;
  for(const i of [1,10,11,8,18,19,88,89,98])board[i]=3;
  for(const r of [3,4,5])for(const c of [3,4,5])if(r!==4||c!==4)board[r*10+c]=3;
  return board;
})();
export function AmazonsBlocked() {
  return <svg viewBox="0 0 308 190" role="img" aria-label="黒の4コマが矢に囲まれて動けなくなると、白の勝ちです。">
    <g transform="translate(7 5)">
      {AMAZONS_END.map((piece,i)=><g key={i} transform={`translate(${i%10*18} ${Math.floor(i/10)*18})`}>
        <rect width="18" height="18" fill={(i%10+Math.floor(i/10))%2?'#eeeade':PAPER} stroke="#d3d0c5" strokeWidth=".5" />
        {(piece===1||piece===2)&&<circle cx="9" cy="9" r="6.7" fill={piece===1?PAPER:INK} stroke={INK} strokeWidth=".8" />}
        {piece===3&&<path d="M5 5L13 13M13 5L5 13" stroke={INK} strokeWidth="1.3" />}
      </g>)}
    </g><text x="201" y="91" fontSize="14">白の勝ちです。</text>
  </svg>;
}

export function AbaloneMoving() {
  return <svg viewBox="0 0 308 101" role="img" aria-label="隣り合った一直線の黒3個が、右上へ1マスずつ横移動します。">
    {[0,1].map(after=><g key={after} transform={`translate(${after*154+7} 0)`}>
      {[0,1].map(row=>[0,1,2].map(i=><g key={`${row}-${i}`}><circle cx={22+i*34+(row?0:17)} cy={row?59:29} r="14" fill="none" stroke="#b5b8ac" />{row===(after?0:1)&&<circle cx={22+i*34+(row?0:17)} cy={row?59:29} r="11" fill={INK} />}</g>))}
      <text x="65" y="97" textAnchor="middle" fontSize="13">{after?'動いた後':'動く前'}</text>
    </g>)}
  </svg>;
}
export function AbaloneStrength() {
 return <svg viewBox="0 18 308 93" role="img" aria-label="黒が押す例です。自分の黒3個で相手の白2個を、列に沿って空きマスへ押せます。同じ数では押せません。">
 {[0,1].map(equal=><g key={equal} transform={`translate(${equal*154} 0)`}>
 {Array.from({length:6},(_,i)=><g key={i}><circle cx={15+i*22} cy="41" r="10" fill="none" stroke="#b5b8ac" />{i<(equal?4:5)&&<circle cx={15+i*22} cy="41" r="8.5" fill={i<(equal?2:3)?INK:PAPER} stroke={INK} />}</g>)}
 <path d={`M${equal?15:15} 59H${equal?93:115}m-5 -4l5 4 -5 4`} fill="none" stroke="#73766e" />
 {Boolean(equal)&&<path d="M53 53l12 12M65 53L53 65" stroke="#a56a5d" strokeWidth="1.5" />}
 <text x="73" y="106" textAnchor="middle" fontSize="14">{equal?'同じ数では押せません。':'3個で2個を押せます。'}</text>
 </g>)}
 </svg>;
}

export function GomokuFirstThree() {
  return <svg viewBox="0 0 308 56" role="img" aria-label="仮先が1手目の黒、2手目の白、3手目の黒を置きます。">
    {[false,true,false].map((white,i)=><g key={i} data-opening-ply={i+1}><circle cx={78+i*76} cy="17" r="10" fill={white?PAPER:INK} stroke={INK} /><text x={78+i*76} y="44" textAnchor="middle" fontSize="13">{i+1}手目</text></g>)}
  </svg>;
}

export function GomokuOpening() {
  return <svg viewBox="0 0 308 204" role="img" aria-label="仮後は黒か白を選ぶか、4手目の白と5手目の黒を置きます。後者では仮先が黒か白を選びます。">
    <path d="M154 7V23H77V38m77 -15H231V38M72 33l5 5 5 -5M226 33l5 5 5 -5" fill="none" stroke="#73766e" />
    <text x="77" y="61" textAnchor="middle" fontSize="14">自分が黒か白かを<tspan x="77" dy="21">選びます。</tspan></text>
    <text x="231" y="61" textAnchor="middle" fontSize="14">2個置きます。</text>
    {[true,false].map((white,i)=><g key={i} data-opening-ply={i+4}><circle cx={208+i*46} cy="98" r="10" fill={white?PAPER:INK} stroke={INK} /><text x={208+i*46} y="122" textAnchor="middle" fontSize="13">{i+4}手目</text></g>)}
    <path d="M231 130V146m-5 -5l5 5 5 -5" fill="none" stroke="#73766e" />
    <text x="231" y="168" textAnchor="middle" fontSize="14">仮先が自分の色を<tspan x="231" dy="22">黒か白から選びます。</tspan></text>
  </svg>;
}

export function MoveAtlas({specs,label='上が前です。点へ1歩進みます。'}:{specs:MoveSpec[];label?:string}) {
 return <figure className="rules-atlas"><div className="rules-atlas-grid">{specs.map(spec=><div key={spec.name} className="rules-atlas-piece"><svg viewBox="0 0 92 88" role="img" aria-label={`${spec.name}の動き。点が行き先、矢印が進める方向です。`}>
 {(()=>{const n=spec.size??5,cell=66/(spec.intersection?n-1:n),mid=46,cy=36;return <>
 {Array.from({length:spec.intersection?n:n+1},(_,i)=><g key={i} stroke="#d0cdc2" strokeWidth=".6"><path d={`M13 ${cy-33+i*cell}H79`} /><path d={`M${13+i*cell} ${cy-33}V${cy+33}`} /></g>)}
 {spec.paths?.map((path,i)=><polyline key={i} points={[[0,0],...path].map(([x,y])=>`${mid+x*cell},${cy+y*cell}`).join(' ')} fill="none" stroke="#aaa99e" strokeWidth=".8" />)}
 {spec.rays?.map(([dx,dy])=>{const x=mid+dx*31,y=cy+dy*31,len=Math.hypot(dx,dy),ux=dx/len,uy=dy/len;return <g key={`${dx},${dy}`} stroke="#6c7768" strokeWidth="1.5" fill="none"><path d={`M${mid} ${cy}L${x} ${y}`} /><path d={`M${x-ux*5-uy*3} ${y-uy*5+ux*3}L${x} ${y}L${x-ux*5+uy*3} ${y-uy*5-ux*3}`} /></g>;})}
 {spec.steps?.map(([dx,dy])=><circle key={`${dx},${dy}`} cx={mid+dx*cell} cy={cy+dy*cell} r="3" fill="#586553" />)}
 {spec.captures?.map(([dx,dy])=><circle key={`${dx},${dy}`} cx={mid+dx*cell} cy={cy+dy*cell} r="3.5" fill={PAPER} stroke="#b42331" />)}
 <circle cx={mid} cy={cy} r="10" fill={PAPER} /><text x={mid} y={cy+6} textAnchor="middle" fontSize={spec.glyph.charCodeAt(0)>0x2650&&spec.glyph.charCodeAt(0)<0x2660?24:17}>{spec.glyph}</text><text x="46" y="85" textAnchor="middle" fontSize="13">{spec.name}</text></>;})()}
 </svg></div>)}</div><figcaption>{label}</figcaption></figure>;
}

export function ReversiFlip() {
  return <svg viewBox="0 0 308 120" role="img" aria-label="黒、白、白の右に黒を置くと、白2枚が裏返り、黒4枚になります。">
    {[0,1].map(row=><g key={row} transform={`translate(0 ${row*62})`}>
      <text x="5" y="29" fontSize="13">{row?'置いた後':'置く前'}</text>
      {[0,1,2,3].map(i=><g key={i}><rect x={92+i*42} y="5" width="42" height="42" fill="none" stroke="#c4c0b2" />{(row||i<3)&&<circle cx={113+i*42} cy="26" r="15" fill={row||i===0?INK:PAPER} stroke={INK} />}</g>)}
    </g>)}
  </svg>;
}

export function TwixtConnections() {
  const points: Point[] = [...Array.from({length:10},(_,i)=>[3+i*2,i] as Point),[22,11],...Array.from({length:10},(_,i)=>[20-i*2,12+i] as Point),[3,23]];
  return <svg viewBox="0 0 308 158" role="img" aria-label="黒が上下を橋で結んだ形と、白が左右を橋で結んだ形は勝ちです。橋は縦横1点と2点ずれた穴を結びます。">
    {[0,1].map(i=><g key={i} transform={`translate(${i*154} 0)`}>
      <rect x="8" y="5" width="126" height="126" fill="none" stroke="#c7c3b6" />
      <path d={i?'M8 5V131M134 5V131':'M8 5H134M8 131H134'} stroke={INK} strokeWidth="3" />
      <polyline points={points.map(([x,y])=>`${8+(i?y:x)*126/23},${5+(i?x:y)*126/23}`).join(' ')} stroke="#626e5b" fill="none" strokeWidth="2" />
      {points.map(([x,y],j)=><circle key={j} cx={8+(i?y:x)*126/23} cy={5+(i?x:y)*126/23} r="2.7" fill={i?PAPER:INK} stroke={INK} strokeWidth="1" />)}
      <text x="71" y="154" textAnchor="middle" fontSize="13">{i?'白が勝ちます。':'黒が勝ちます。'}</text>
    </g>)}
  </svg>;
}

export function AnimalGoals() {
 return <svg viewBox="0 0 308 147" role="img" aria-label="左はきりんで相手のライオンを取ります。右は自分のライオンが安全に相手の奥の段へ入ります。">
 {[0,1].map(i=><g key={i} transform={`translate(${i*154+18} 2)`}>
 {[0,1,2,3].map(j=><path key={j} d={`M${j*27} 0V108`} stroke="#c3bcaa" />)}{[0,1,2,3,4].map(j=><path key={j} d={`M0 ${j*27}H81`} stroke="#c3bcaa" />)}
 {i===1&&<rect width="81" height="27" fill="#e3e8db" opacity=".7" />}
 <text x={i?13.5:40.5} y={i?103:22} textAnchor="middle" fontSize="19" transform={i?'rotate(180 13.5 94.5)':'rotate(180 40.5 13.5)'}>ラ</text>
 <text x="40.5" y="49" textAnchor="middle" fontSize="19">{i?'ラ':'キ'}</text>
 {!i&&<text x="13.5" y="103" textAnchor="middle" fontSize="19">ラ</text>}
 <path d="M40.5 34V24m-4 4l4 -4 4 4" fill="none" stroke="#626e5b" strokeWidth="1.7" />
 <text x="40.5" y="138" textAnchor="middle" fontSize="13">{i?'安全に奥へ入る':'ライオンを取る'}</text>
 </g>)}
 </svg>;
}
export function TwixtBridge() {
 return <svg viewBox="0 0 308 127" role="img" aria-label="左は縦2点横1点ずれた同色の駒を橋で結びます。右は相手の橋と交差する橋を作れません。">
 {[0,1].map(i=><g key={i} transform={`translate(${i*154+24} 2)`}>
 {[0,1,2].map(r=>[0,1,2].map(c=><circle key={`${r},${c}`} cx={c*35} cy={r*35} r="2" fill="#b8baae" />))}
 <path d="M0 70L35 0" stroke="#626e5b" strokeWidth="2.5" />{[[0,70],[35,0]].map(([x,y])=><circle key={`${x},${y}`} cx={x} cy={y} r="5" fill={INK} />)}
 {Boolean(i)&&<><path d="M0 0L35 70" stroke="#a56a5d" strokeWidth="2" strokeDasharray="4 3" />{[[0,0],[35,70]].map(([x,y])=><circle key={`${x},${y}`} cx={x} cy={y} r="5" fill={PAPER} stroke={INK} />)}<path d="M9 28l15 15M24 28L9 43" stroke="#a56a5d" strokeWidth="2" /></>}
 <text x="35" y="100" textAnchor="middle" fontSize="13">{i?'相手の橋は跨げません。':'縦横に1点と2点'}</text>
 </g>)}
 </svg>;
}
